import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'umi';

// 样式
import './index.scss';

// 图片资源
import agentRobotIcon from './imgs/agent-robot.png';
import agentRobotBlinkIcon from './imgs/agent-robot-blink.png';
import agentIcon from './imgs/chevron-left.png';

// 工具函数
import { storage } from '@/utils/utils';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { recordLeaveTime } from '@home/utils/bubbleHelpers';

// 组件
import { Bubble } from '../TeaComponents';
import Copilot from '@/pages/aigc/pages/Agent/Copilot';

// Hooks 和常量
import { useLocalState, store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { AGENT_ROBOT_EXPEND_STATE } from '@home/_config/config';
import { COPILOT_REPORT_WINDOW_STATE_KEY } from '@/pages/aigc/pages/Agent/config/constant';

// 自定义 Hooks
import { useBubbleLogic } from './hooks/useBubbleLogic';
import { useBlinkAnimation } from './hooks/useBlinkAnimation';
import { useForceCopilotPopup } from './hooks/useForceCopilotPopup';

const Agent: React.FC = () => {
  const expanded = storage.getItem(AGENT_ROBOT_EXPEND_STATE);
  const [isExpanded, setIsExpanded] = useState(expanded === null ? true : expanded);
  const { formatMessage } = useIntl();
  const [isShowCopilot, setIsShowCopilot] = useLocalState('__dba_show_copilot__', false);

  // 报告窗口状态（持久化），用于判断是否需要单独显示报告窗口
  const [reportWindowState] = useLocalState<{ visible: boolean; } | null>(COPILOT_REPORT_WINDOW_STATE_KEY, null);

  // 使用自定义 Hooks 处理气泡逻辑和眨眼动画
  const { showBubble, bubblePrompt, permanentlyDismissBubble } = useBubbleLogic();

  // 强制弹窗（落地页强引导）：仅在三大页 + 当日首次访问时触发
  // 复用 Copilot 面板组件，无独立 UI；本 Hook 仅负责触发与状态管理
  useForceCopilotPopup();

  // 记录用户离开时刻：切走 Tab 或关闭浏览器时写入 localStorage
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) recordLeaveTime();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', recordLeaveTime);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', recordLeaveTime);
    };
  }, []);
  const { isBlinking } = useBlinkAnimation(showBubble);

  // Robot 点击事件处理
  const handleClickRobot = useCallback(() => {
    buriedBtnPoint({
      buttonId: '100101015',
      extInfo: 'jump to agent',
      pageId: '100101',
      typeId: 'homePage_pc_v2',
    });
    // 只有在关闭 Copilot 且没有报告窗口打开时才清空 sessionId
    // 如果报告窗口还在显示，保留 sessionId
    if (isShowCopilot && !reportWindowState?.visible) {
      store.setItem('__dba_chat_copilot__', '');
    }
    setIsShowCopilot(!isShowCopilot);
  }, [isShowCopilot, reportWindowState?.visible]);

  // 气泡关闭按钮点击事件
  const handleBubbleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    permanentlyDismissBubble();
  }, [permanentlyDismissBubble]);

  // 展开/收起切换事件
  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
    storage.setItem(AGENT_ROBOT_EXPEND_STATE, !isExpanded);
  }, [isExpanded]);

  // 判断是否需要显示 Copilot 组件：
  // 1. Copilot 窗口打开时
  // 2. 或者报告窗口单独打开时（Copilot 关闭但报告窗口还在）
  const shouldShowCopilot = isShowCopilot || reportWindowState?.visible;

  return (
    <>
      {!isShowCopilot && (
        <>
          <div
            className={`dbh-agent-wrapper ${!isExpanded ? 'dbh-agent-collapsed' : ''}`}
          >
            <Bubble
              content={formatMessage({ id: 'dbh_login_robot_hover' })}
              placement="left"
              dark
            >
              <div
                className="dbh-agent-robot-icon-wrapper"
                onClick={handleClickRobot}
              >
                <img
                  className="dbh-agent-robot-icon"
                  src={agentRobotIcon}
                  alt="agent-robot"
                />
                <img
                  className={`dbh-agent-robot-icon dbh-agent-robot-icon--blink ${isBlinking ? 'dbh-agent-robot-icon--blink-active' : ''}`}
                  src={agentRobotBlinkIcon}
                  alt="agent-robot-blink"
                />
              </div>
            </Bubble>
            <Bubble
              content={formatMessage({
                id: isExpanded
                  ? 'dbh_login_arrow_collapse'
                  : 'dbh_login_arrow_expand',
              })}
              placement="top-end"
              overlayClassName="dbh-robot-arrow"
              placementOffset={[0, 10]}
            >
              <div className="dbh-agent-icon-wrapper">
                <img
                  src={agentIcon}
                  className="dbh-agent-icon"
                  alt="agent-icon"
                  onClick={handleToggleExpand}
                />
              </div>
            </Bubble>
          </div>
          {/* 自定义 fixed 定位的 greeting 气泡，箭头精准对准 robot 图标中轴线 */}
          {showBubble && (
            <div className="dbh-agent-greeting-popup">
              <div className="dbh-agent-bubble-content" onClick={handleClickRobot}>
                <div className="dbh-agent-bubble-text">
                  {bubblePrompt}
                </div>
                <span
                  className="dbh-agent-bubble-close"
                  onClick={handleBubbleClose}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.99609 18L18.0006 5.99552" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M18 18L5.99552 5.99553" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </span>
              </div>
            </div>
          )}
        </>
      )}
      {shouldShowCopilot && (
        <Copilot 
          handleClickRobot={handleClickRobot} 
          isShowCopilot={isShowCopilot}
        />
      )}
    </>
  );
};

export default Agent;
